/**
 * 图片加水印通用工具
 * 功能：将原始图片转为带水印的图片URL（Base64），支持平铺/单水印、自定义配置
 * 适用：Vue/React/原生JS项目，无需框架依赖
 * 举例：<img class="img-responsive" alt="宣传图"  :src="$setWatermark(watermarkImgs.news, {
      text: 'AIFC',
      repeat: false,
      fontSize: 32,
      rotate: 0,
      spacingX:10,
      spacingY:0,
    })"/>
 */
const watermarkCache = new Map();

// 生成配置哈希
const getOptionsHash = options => JSON.stringify(options);

// 核心：异步生成水印URL
const generateWatermarkedImageUrl = (imgUrl, options = {}) => {
  return new Promise((resolve, reject) => {
    const defaultOptions = {
      text: "默认水印",
      fontSize: 24,
      rotate: -20,
      opacity: 0.5,
      color: "rgba(180,180,180,0.5)", //#b4b4b4
      repeat: true,
      position: "bottom-right",
      spacingX: 180,
      spacingY: 120,
      edgeSpacingX: 40, // 控制左侧距离
      edgeSpacingY: 40, // 控制顶部距离
      crossOrigin: "anonymous"
    };
    const opts = { ...defaultOptions, ...options };

    const img = new Image();
    img.crossOrigin = opts.crossOrigin;

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        drawWatermark(ctx, canvas.width, canvas.height, opts);
        resolve(canvas.toDataURL("image/png"));
        canvas.width = 0;
        canvas.height = 0;
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = err => reject(err);
    img.src = imgUrl;
  });
};
// 绘制水印
const drawWatermark = (ctx, canvasWidth, canvasHeight, options) => {
  ctx.fillStyle = options.color;
  ctx.globalAlpha = options.opacity;
  ctx.font = `${options.fontSize}px Microsoft YaHei`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const rotateRadian = (options.rotate * Math.PI) / 180;

  if (options.repeat) {
    ctx.save();
    // 1. 计算文字基础尺寸
    const textWidth = ctx.measureText(options.text).width;
    const textHeight = options.fontSize;

    // 2. 计算旋转后文字「向左侧/上侧的最小偏移量」（仅补偿截断部分，不过度偏移）
    // 向左偏移量 = 文字宽*|cosθ|/2 + 文字高*|sinθ|/2 - 文字宽/2
    const leftOffset =
      (textWidth * Math.abs(Math.cos(rotateRadian)) +
        textHeight * Math.abs(Math.sin(rotateRadian))) /
        2 -
      textWidth / 2;
    // 向上偏移量 = 文字高*|cosθ|/2 + 文字宽*|sinθ|/2 - 文字高/2
    const topOffset =
      (textHeight * Math.abs(Math.cos(rotateRadian)) +
        textWidth * Math.abs(Math.sin(rotateRadian))) /
        2 -
      textHeight / 2;

    // 3. 计算平铺起始点（核心：edgeSpacingX/Y 控制边缘距离，仅补偿截断部分）
    // 第一列起始X = 边缘距离 - 左侧截断偏移（确保第一列紧贴左侧且完整）
    const startX = options.edgeSpacingX - Math.max(0, leftOffset);
    // 第一排起始Y = 边缘距离 - 顶部截断偏移（确保第一排紧贴顶部且完整）
    const startY = options.edgeSpacingY - Math.max(0, topOffset);
    // 循环终点：画布宽/高 + 边缘距离（避免右侧/底部截断）
    const endX = canvasWidth + options.edgeSpacingX;
    const endY = canvasHeight + options.edgeSpacingY;

    // 4. 平铺循环：起始点精准控制，无过度偏移
    for (let x = startX; x < endX; x += options.spacingX) {
      for (let y = startY; y < endY; y += options.spacingY) {
        ctx.translate(x, y);
        ctx.rotate(rotateRadian);
        ctx.fillText(options.text, 0, 0);
        ctx.rotate(-rotateRadian);
        ctx.translate(-x, -y);
      }
    }
    ctx.restore();
  } else {
    // 单水印逻辑
    let x = 0,
      y = 0;
    const textWidth = ctx.measureText(options.text).width;
    const textHeight = options.fontSize;

    switch (options.position) {
      case "top-left":
        x = options.spacingX + textWidth / 2;
        y = options.spacingY + textHeight / 2;
        break;
      case "top-right":
        x = canvasWidth - options.spacingX - textWidth / 2;
        y = options.spacingY + textHeight / 2;
        break;
      case "bottom-left":
        x = options.spacingX + textWidth / 2;
        y = canvasHeight - options.spacingY - textHeight / 2;
        break;
      case "bottom-right":
        // 先计算基础坐标（无补偿，贴底核心）
        x = canvasWidth - options.spacingX - textWidth / 2;
        y = canvasHeight - options.spacingY - textHeight / 2;

        // 仅当旋转角度≠0时，添加补偿（避免水印超出图片）
        if (options.rotate !== 0) {
          x -= Math.abs((Math.sin(rotateRadian) * textHeight) / 2);
          y -= Math.abs((Math.cos(rotateRadian) * textWidth) / 2);
        }
        break;
      case "center":
        x = canvasWidth / 2;
        y = canvasHeight / 2;
        break;
    }

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotateRadian);
    ctx.fillText(options.text, 0, 0);
    ctx.restore();
  }
};

// 对外暴露的方法：只接收已解析的图片URL
export const setWatermark = (imgUrl, options = {}) => {
  const cacheKey = `${imgUrl}_${getOptionsHash(options)}`;

  // 有缓存直接返回
  if (watermarkCache.has(cacheKey)) {
    return watermarkCache.get(cacheKey);
  }

  // 无缓存则异步生成
  generateWatermarkedImageUrl(imgUrl, options)
    .then(watermarkedUrl => {
      watermarkCache.set(cacheKey, watermarkedUrl);
      document.querySelectorAll(`img[src="${imgUrl}"]`).forEach(img => {
        img.src = watermarkedUrl;
      });
    })
    .catch(err => {
      console.error("生成水印失败：", err);
      watermarkCache.set(cacheKey, imgUrl);
    });

  // 首次返回原始URL
  return imgUrl;
};



// WEBPACK FOOTER //
// ./src/utils/watermark.js